"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  setIsCollapsed,
  setIsHydrated,
  setIsMobileSidebarOpen,
} from "@/redux/slices/sidebarSlice";
import {
  isSidebarCompactNav,
  isShellSidebarDesktopViewport,
  markKeepShellCompactAfterNav,
  shellSidebarWidthClass,
} from "@/lib/layout/shellSidebarLayout";
import {
  ArrowLeftRight,
  BotIcon,
  Rocket,
  Flame,
  Sparkles,
  UploadCloud as UploadCloudIcon,
  Droplets,
  Gem,
  BarChart3,
  ShieldOff,
  Tags,
  FlaskConical,
  CandlestickChart,
  LucideLayoutDashboard,
  Percent,
} from "lucide-react";
import { useAuthModal } from "@/hooks/useAuthModal";
import { ALL_VOLUME_BOT_LANDING_PATHS } from "@/config/volumeBotLandingConfig";
import { IDX_EXCHANGE_URL } from "@/config/idxExchangeAppConfig";
import {
  SidebarCompactOverlayPortal,
  createCompactTipHandlers,
  createCompactFlyoutHandlers,
} from "@/components/layout/SidebarCompactTooltip";

const SIDEBAR_BADGE_STYLES = {
  New: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/25",
};

const SIDEBAR_ROW_FLEX =
  "group relative flex w-full items-center py-2.5 rounded-lg transition-colors duration-200";
const SIDEBAR_TWO_COL_GRID =
  "grid w-full grid-cols-[3rem_minmax(0,1fr)] lg:grid-cols-[4rem_minmax(0,1fr)]";
const SIDEBAR_ROW_GRID =
  "group relative grid w-full grid-cols-[3rem_minmax(0,1fr)] lg:grid-cols-[4rem_minmax(0,1fr)] items-center py-2.5 rounded-lg transition-colors duration-200";
const SIDEBAR_ICON_RAIL =
  "flex w-12 shrink-0 items-center justify-center lg:w-16";
const SIDEBAR_SHELL_TRANSITION = "transition-[width] duration-300 ease-in-out";

/** Pre-hydrate: collapse labels on mobile so SSR matches the icon rail; keep desktop labels. */
const PRE_HYDRATE_LABEL_MOBILE_COLLAPSE =
  "max-lg:max-w-0 max-lg:flex-none max-lg:opacity-0 max-lg:pointer-events-none max-lg:translate-x-1";

function sidebarLabelCellClass(expanded, className = "", preHydrate = false) {
  return `flex min-w-0 items-center overflow-hidden pr-3 ${
    expanded
      ? "opacity-100 translate-x-0 transition-[opacity,transform] duration-300 ease-out delay-75"
      : "max-w-0 opacity-0 translate-x-1 pointer-events-none transition-[opacity,transform] duration-150 ease-in"
  } ${preHydrate ? PRE_HYDRATE_LABEL_MOBILE_COLLAPSE : ""} ${className}`;
}

function SidebarIconRail({ active, children }) {
  return (
    <div className={SIDEBAR_ICON_RAIL}>
      <div className={`flex-shrink-0 ${active ? "text-emerald-500" : ""}`}>
        {children}
      </div>
    </div>
  );
}

function SidebarLabelPanel({ expanded, preHydrate = false, children, className = "" }) {
  return (
    <div
      className={`flex min-w-0 items-center overflow-hidden ${
        expanded
          ? "max-w-[11rem] flex-1 opacity-100 translate-x-0 transition-[max-width,opacity,transform] duration-300 ease-out delay-75"
          : "max-w-0 flex-none opacity-0 translate-x-1 pointer-events-none transition-[max-width,opacity,transform] duration-150 ease-in"
      } ${preHydrate ? PRE_HYDRATE_LABEL_MOBILE_COLLAPSE : ""} ${className}`}
      {...(expanded ? {} : { "aria-hidden": true })}
      suppressHydrationWarning
    >
      {children}
    </div>
  );
}

function SidebarSubmenu({ id, children }) {
  return (
    <div
      id={id}
      className={`${SIDEBAR_TWO_COL_GRID} mt-1.5 animate-in fade-in slide-in-from-top-1 duration-300 ease-out fill-mode-both`}
    >
      <div aria-hidden />
      <div className="min-w-0 space-y-1 pr-3">{children}</div>
    </div>
  );
}

function SidebarNavBadge({ label, plain = false }) {
  if (!label) return null;
  const style = SIDEBAR_BADGE_STYLES[label] ?? SIDEBAR_BADGE_STYLES.New;
  const compact = label === "New Beta" || label === "Beta";
  return (
    <span
      className={`flex-shrink-0 font-semibold uppercase ${
        plain
          ? "text-[10px] tracking-wide text-emerald-400"
          : `ring-1 ${style} ${
              compact
                ? "rounded px-1 py-px text-[8px] tracking-normal"
                : "rounded-md px-1.5 py-0.5 text-[10px] tracking-wide"
            }`
      }`}
      aria-hidden
    >
      {label}
    </span>
  );
}

const Sidebar = () => {
  const dispatch = useDispatch();
  const { isCollapsed, isHydrated, isMobileSidebarOpen } = useSelector(
    (state) => state.sidebar,
  );
  const [expandedItems, setExpandedItems] = useState([]);
  const [compactOverlay, setCompactOverlay] = useState(null);
  const overlayHideTimer = useRef(null);
  const hoverAnchorRef = useRef(null);
  const submenuHoverCloseTimer = useRef(null);
  /** Sections the user click-closed — hover must not reopen until they click open again. */
  const hoverLockedClosedRef = useRef(new Set());
  /** Submenu opened by expanding the shell from a dropdown icon on the rail. */
  const railOpenedSectionRef = useRef(/** @type {string | null} */ (null));
  const pathname = usePathname();
  const { isAuthenticated, openLoginModal } = useAuthModal();
  const compactNav = isSidebarCompactNav(isMobileSidebarOpen, isCollapsed);
  const showCompactTip = isHydrated && compactNav;
  const labelsExpanded = !isHydrated || !compactNav;
  const labelsPreHydrate = !isHydrated;
  const shellWidthClass = shellSidebarWidthClass(
    isMobileSidebarOpen,
    isCollapsed,
    isHydrated,
  );

  const clearOverlayHide = useCallback(() => {
    if (overlayHideTimer.current) {
      clearTimeout(overlayHideTimer.current);
      overlayHideTimer.current = null;
    }
  }, []);

  const scheduleOverlayHide = useCallback(() => {
    clearOverlayHide();
    overlayHideTimer.current = setTimeout(() => {
      if (hoverAnchorRef.current?.matches?.(":hover")) return;
      setCompactOverlay(null);
      hoverAnchorRef.current = null;
    }, 150);
  }, [clearOverlayHide]);

  const setHoverAnchor = useCallback((node) => {
    hoverAnchorRef.current = node;
  }, []);

  const overlayHoverProps = useMemo(
    () => ({
      scheduleHide: scheduleOverlayHide,
      clearHide: clearOverlayHide,
      setHoverAnchor,
    }),
    [scheduleOverlayHide, clearOverlayHide, setHoverAnchor],
  );

  const compactTipProps = useCallback(
    (label) =>
      createCompactTipHandlers(
        setCompactOverlay,
        showCompactTip,
        label,
        overlayHoverProps,
      ),
    [showCompactTip, overlayHoverProps],
  );

  const compactFlyoutProps = useCallback(
    (item) =>
      createCompactFlyoutHandlers(
        setCompactOverlay,
        showCompactTip,
        item,
        overlayHoverProps,
      ),
    [showCompactTip, overlayHoverProps],
  );

  useEffect(() => {
    if (!showCompactTip) setCompactOverlay(null);
  }, [showCompactTip]);

  useEffect(() => {
    setCompactOverlay(null);
  }, [pathname]);

  useEffect(() => () => clearOverlayHide(), [clearOverlayHide]);

  // Set hydrated state on mount
  useEffect(() => {
    dispatch(setIsHydrated(true));
  }, [dispatch]);

  // Route open/close is handled by ShellSidebarRouteSync (main bundle).

  // Collapse all accordions when the menu closes to the icon rail.
  useEffect(() => {
    if (!compactNav) return;
    setExpandedItems([]);
    hoverLockedClosedRef.current = new Set();
    railOpenedSectionRef.current = null;
  }, [compactNav]);

  // Auto-expand nested nav when on a child route (respect click-closed locks).
  // Also re-run when the shell expands (compactNav → false): dashboard is full-width
  // compact, so pathname alone can fire while still compact and skip expand — then
  // Create Bot → Volume + would land on /volume-bot with Volume bot closed.
  useEffect(() => {
    if (compactNav) return;
    setExpandedItems((prev) => {
      let next = prev;
      const add = (id) => {
        if (hoverLockedClosedRef.current.has(id)) return;
        if (!next.includes(id)) next = [...next, id];
      };
      if (
        pathname === "/revoke-freeze" ||
        pathname === "/revoke-mint-authority"
      ) {
        add("revoke-authorities");
      }
      if (
        pathname === "/token-creator" ||
        pathname === "/token-creator/token-2022"
      ) {
        add("token-creator");
      }
      if (pathname.startsWith("/manage-liquidity")) {
        add("manage-liquidity");
      }
      if (
        ALL_VOLUME_BOT_LANDING_PATHS.some(
          (p) => pathname === p || pathname.startsWith(`${p}/`),
        )
      ) {
        add("volume-bot");
      }
      return next;
    });
  }, [pathname, compactNav]);

  const toggleExpanded = useCallback((itemId) => {
    setExpandedItems((prev) => {
      const isOpen = prev.includes(itemId);
      if (isOpen) {
        hoverLockedClosedRef.current.add(itemId);
        if (railOpenedSectionRef.current === itemId) {
          railOpenedSectionRef.current = null;
        }
      } else {
        hoverLockedClosedRef.current.delete(itemId);
      }
      return isOpen ? prev.filter((i) => i !== itemId) : [...prev, itemId];
    });
  }, []);

  /** Sections that should stay open while on their child routes (menu still expanded). */
  const routePinnedSectionIds = useMemo(() => {
    const ids = [];
    if (
      pathname === "/revoke-freeze" ||
      pathname === "/revoke-mint-authority"
    ) {
      ids.push("revoke-authorities");
    }
    if (
      pathname === "/token-creator" ||
      pathname === "/token-creator/token-2022"
    ) {
      ids.push("token-creator");
    }
    if (pathname.startsWith("/manage-liquidity")) {
      ids.push("manage-liquidity");
    }
    if (
      ALL_VOLUME_BOT_LANDING_PATHS.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`),
      )
    ) {
      ids.push("volume-bot");
    }
    return ids;
  }, [pathname]);

  const syncExpandedToRoutePinned = useCallback(() => {
    const pinned = routePinnedSectionIds.filter(
      (id) => !hoverLockedClosedRef.current.has(id),
    );
    const railOpened = railOpenedSectionRef.current;
    const keep =
      railOpened && !hoverLockedClosedRef.current.has(railOpened)
        ? [...new Set([...pinned, railOpened])]
        : pinned;
    setExpandedItems((prev) => {
      if (
        prev.length === keep.length &&
        keep.every((id) => prev.includes(id))
      ) {
        return prev;
      }
      return keep;
    });
  }, [routePinnedSectionIds]);

  const openExpandedSection = useCallback(
    (itemId) => {
      if (hoverLockedClosedRef.current.has(itemId)) return;
      if (submenuHoverCloseTimer.current) {
        clearTimeout(submenuHoverCloseTimer.current);
        submenuHoverCloseTimer.current = null;
      }
      setExpandedItems((prev) => {
        const pinned = routePinnedSectionIds.filter(
          (id) => !hoverLockedClosedRef.current.has(id),
        );
        if (prev.includes(itemId) && pinned.every((id) => prev.includes(id))) {
          return prev;
        }
        const next = new Set([...pinned, ...prev, itemId]);
        return [...next];
      });
    },
    [routePinnedSectionIds],
  );

  /** Delayed close so moving from label → submenu doesn’t blink shut. */
  const scheduleSubmenuHoverClose = useCallback(() => {
    if (submenuHoverCloseTimer.current) {
      clearTimeout(submenuHoverCloseTimer.current);
    }
    submenuHoverCloseTimer.current = setTimeout(() => {
      submenuHoverCloseTimer.current = null;
      syncExpandedToRoutePinned();
    }, 220);
  }, [syncExpandedToRoutePinned]);

  useEffect(
    () => () => {
      if (submenuHoverCloseTimer.current) {
        clearTimeout(submenuHoverCloseTimer.current);
      }
    },
    [],
  );

  const closeMobileSidebar = useCallback(() => {
    if (isShellSidebarDesktopViewport()) return;
    dispatch(setIsMobileSidebarOpen(false));
    dispatch(setIsCollapsed(true));
  }, [dispatch]);

  /** Active page icon toggles the shell menu: expand if collapsed, collapse if open. */
  const handleNavIconClick = useCallback(
    (e, isActive) => {
      if (!isHydrated || !isActive) {
        closeMobileSidebar();
        return;
      }
      e.preventDefault();
      if (compactNav) {
        dispatch(setIsCollapsed(false));
        dispatch(setIsMobileSidebarOpen(true));
      } else {
        dispatch(setIsCollapsed(true));
        dispatch(setIsMobileSidebarOpen(false));
      }
    },
    [isHydrated, compactNav, closeMobileSidebar, dispatch],
  );

  const handleDashboardClick = useCallback(
    (e) => {
      if (!isAuthenticated) {
        e.preventDefault();
        openLoginModal();
        return;
      }
      handleNavIconClick(e, pathname === "/dashboard");
    },
    [isAuthenticated, openLoginModal, handleNavIconClick, pathname],
  );

  /** Icon column: expand/collapse the shell; when expanding from rail, also open that dropdown's submenu. */
  const toggleShellMenu = useCallback(
    (itemId) => {
      if (compactNav) {
        if (submenuHoverCloseTimer.current) {
          clearTimeout(submenuHoverCloseTimer.current);
          submenuHoverCloseTimer.current = null;
        }
        dispatch(setIsCollapsed(false));
        dispatch(setIsMobileSidebarOpen(true));
        if (itemId) {
          railOpenedSectionRef.current = itemId;
          hoverLockedClosedRef.current.delete(itemId);
          setExpandedItems((prev) => {
            const pinned = routePinnedSectionIds.filter(
              (id) => !hoverLockedClosedRef.current.has(id),
            );
            return [...new Set([...pinned, ...prev, itemId])];
          });
        }
      } else {
        railOpenedSectionRef.current = null;
        dispatch(setIsCollapsed(true));
        dispatch(setIsMobileSidebarOpen(false));
      }
      setCompactOverlay(null);
    },
    [compactNav, dispatch, routePinnedSectionIds],
  );

  const menuItems = useMemo(
    () => [
      {
        id: "dashboard",
        name: "Bot Dashboard",
        icon: <LucideLayoutDashboard className="w-5 h-5 flex-shrink-0" />,
        href: "/dashboard",
        active: true,
      },
    ],
    [],
  );

  const activeTools = useMemo(
    () => [
      {
        id: "exchange",
        name: "IDX Exchange",
        icon: <CandlestickChart className="w-5 h-5 flex-shrink-0" />,
        href: IDX_EXCHANGE_URL,
        external: true,
      },
      {
        id: "volume-bot",
        name: "Volume Bot",
        icon: <BotIcon className="w-5 h-5 flex-shrink-0" />,
        hasDropdown: true,
        children: [
          {
            id: "volume-bot-general",
            name: "General bot",
            href: "/volume-bot",
          },
          {
            id: "volume-bot-idx",
            name: "IDX bot",
            href: "/idx-volume-bot",
            iconSrc: "/idx-logo-mark.png",
            iconAlt: "IDX Logo black Background",
          },
          {
            id: "volume-bot-pumpfun",
            name: "PumpFun bot",
            href: "/pumpfun-volume-bot",
            iconSrc: "/pools/pumpfun.png",
            iconAlt: "Pump.fun",
          },
          {
            id: "volume-bot-raydium",
            name: "Raydium bot",
            href: "/raydium-volume-bot",
            iconSrc: "/pools/raydium.png",
            iconAlt: "Raydium",
          },
          {
            id: "volume-bot-orca",
            name: "Orca bot",
            href: "/orca-volume-bot",
            iconSrc: "/pools/orca.png",
            iconAlt: "Orca",
          },
          {
            id: "volume-bot-meteora",
            name: "Meteora bot",
            href: "/meteora-volume-bot",
            iconSrc: "/pools/meteora.png",
            iconAlt: "Meteora",
          },
        ],
      },
      {
        id: "holders-maker",
        name: "Holders Maker",
        icon: (
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        ),
        href: "/holders-maker",
      },
      {
        id: "reaction-booster",
        name: "Reaction Booster",
        icon: <Rocket className="w-5 h-5 flex-shrink-0" />,
        href: "/reaction-booster",
      },
      {
        id: "smart-sell",
        name: "Smart Sell",
        icon: <Percent className="w-5 h-5 flex-shrink-0" />,
        href: "/smart-sell",
        badge: "Beta",
      },
      {
        id: "idx-launchlab",
        name: "Launch Lab",
        icon: <FlaskConical className="w-5 h-5 flex-shrink-0" />,
        href: "/idx-launchlab",
        matchChildren: true,
      },
      {
        id: "token-hub",
        name: "Token Hub",
        icon: <Gem className="w-5 h-5 flex-shrink-0" />,
        href: "/token-hub",
      },
      {
        id: "the-token-burn",
        name: "Token Burn",
        icon: <Flame className="w-5 h-5 flex-shrink-0" />,
        href: "/token-burn",
      },
      {
        id: "bulk-sender",
        name: "Bulk Sender",
        icon: (
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M12 2v8" />
            <path d="M2 15c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
            <path d="m8 6 4-4 4 4" />
          </svg>
        ),
        href: "/bulk-sender/solana",
      },
      {
        id: "update-metadata",
        name: "Update Token Metadata",
        icon: <UploadCloudIcon className="w-5 h-5 flex-shrink-0" />,
        href: "/update-metadata",
      },
      {
        id: "token-creator",
        name: "Create Token Pro",
        icon: <Sparkles className="w-5 h-5 flex-shrink-0" />,
        hasDropdown: true,
        children: [
          {
            id: "token-creator-spl",
            name: "Create SPL Token",
            href: "/token-creator",
            exactMatch: true,
          },
          {
            id: "token-creator-2022",
            name: "Create Token-2022 (Tax)",
            href: "/token-creator/token-2022",
          },
        ],
      },
      {
        id: "liquidity-pool-creator",
        name: "Create Liquidity Pool",
        icon: <Droplets className="w-5 h-5 flex-shrink-0" />,
        href: "/liquidity-pool-creator",
      },
      {
        id: "manage-liquidity",
        name: "Manage Liquidity",
        icon: <Tags className="w-5 h-5 flex-shrink-0" />,
        hasDropdown: true,
        children: [
          {
            id: "add-liquidity",
            name: "Add Liquidity",
            href: "/manage-liquidity/add-liquidity",
          },
          {
            id: "remove-liquidity",
            name: "Remove Liquidity",
            href: "/manage-liquidity/remove-liquidity",
          },
        ],
      },
      // {
      //   id: "lock-liquidity",
      //   name: "Lock Liquidity",
      //   icon: <Lock className="w-5 h-5 flex-shrink-0" />,
      //   hasDropdown: true,
      //   children: [
      //     {
      //       id: "lock-liquidity-create",
      //       name: "Create Lock",
      //       href: "/lock-liquidity",
      //     },
      //     {
      //       id: "lock-liquidity-my-locks",
      //       name: "My Locked Tokens",
      //       href: "/lock-liquidity/my-locks",
      //     },
      //   ],
      // },
      // {
      //   id: "create-openbook-market",
      //   name: "Create OpenBook Market",
      //   icon: <BookOpen className="w-5 h-5 flex-shrink-0" />,
      //   href: "/create-openbook-market",
      // },
      {
        id: "liquidity-simulator",
        name: "Liquidity Simulator",
        icon: <BarChart3 className="w-5 h-5 flex-shrink-0" />,
        href: "/liquidity-pool-creator/liquidity-simulator",
      },
      {
        id: "revoke-authorities",
        name: "Revoke Authorities",
        icon: <ShieldOff className="w-5 h-5 flex-shrink-0" />,
        hasDropdown: true,
        children: [
          {
            id: "revoke-mint-authority",
            name: "Revoke Mint Authority",
            href: "/revoke-mint-authority",
          },
          {
            id: "revoke-freeze",
            name: "Revoke Freeze Authority",
            href: "/revoke-freeze",
          },
        ],
      },
      {
        id: "fast-swap",
        name: "Fast Swap",
        icon: <ArrowLeftRight className="w-5 h-5 flex-shrink-0" />,
        href: "/fast-swap",
      },
    ],
    [],
  );

  const upcomingTools = useMemo(() => [], []);

  // Check if item is active
  const isItemActive = useCallback(
    (href) => {
      if (!href) return false;
      return pathname === href;
    },
    [pathname],
  );

  /** Top-level tool links: optional prefix match for nested routes */
  const isActiveToolLink = useCallback(
    (item) => {
      if (!item?.href) return false;
      if (item.matchChildren) {
        return pathname === item.href || pathname.startsWith(`${item.href}/`);
      }
      return pathname === item.href;
    },
    [pathname],
  );

  /** Sidebar submenu links; use exactMatch for leaf routes that should not match deeper paths */
  const isSidebarChildActive = useCallback(
    (child) => {
      if (!child?.href) return false;
      if (child.exactMatch) return pathname === child.href;
      return pathname === child.href || pathname.startsWith(`${child.href}/`);
    },
    [pathname],
  );

  return (
    <>
      <SidebarCompactOverlayPortal
        overlay={showCompactTip ? compactOverlay : null}
        onOverlayEnter={clearOverlayHide}
        onOverlayLeave={scheduleOverlayHide}
        isChildActive={isSidebarChildActive}
        onChildNavigate={() => {
          // Stay on icon rail after picking a flyout child — do not expand the shell.
          markKeepShellCompactAfterNav();
          setCompactOverlay(null);
          hoverAnchorRef.current = null;
          closeMobileSidebar();
        }}
      />
      {isMobileSidebarOpen && !compactNav && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-40 bg-black/60 backdrop-blur-sm sm:top-16 lg:hidden"
          onClick={closeMobileSidebar}
          aria-hidden
        />
      )}

      {/* Below global header only — no second chrome row (logo / close / collapse). */}
      <div
        className={`fixed bottom-0 left-0 top-14 z-50 flex flex-col overflow-hidden border-r border-gray-800/50 bg-gray-900 translate-x-0 sm:top-16 ${
          isHydrated ? SIDEBAR_SHELL_TRANSITION : ""
        } ${shellWidthClass}`}
        data-shell-compact={compactNav ? "1" : "0"}
      >
        {/* Navigation */}
        <nav className="sidebar-nav-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden pt-2 pb-4 md:pt-3">
          <div className="space-y-1">
            {/* Dashboard */}
            <div>
              <Link
                href="/dashboard"
                prefetch={false}
                aria-label={menuItems[0].name}
                className={`${SIDEBAR_ROW_FLEX} ${
                  isItemActive("/dashboard")
                    ? "text-white bg-gray-800 shadow-sm"
                    : "text-gray-300 hover:text-white hover:bg-gray-800/50"
                }`}
                onClick={handleDashboardClick}
                {...compactTipProps(menuItems[0].name)}
                rel="noopener noreferrer"
              >
                {isItemActive("/dashboard") && (
                  <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-emerald-600" />
                )}
                <SidebarIconRail active={isItemActive("/dashboard")}>
                  {menuItems[0].icon}
                </SidebarIconRail>
                <SidebarLabelPanel
                  expanded={labelsExpanded}
                  preHydrate={labelsPreHydrate}
                >
                  <span className="truncate text-sm font-medium tracking-wide">
                    {menuItems[0].name}
                  </span>
                </SidebarLabelPanel>
              </Link>
            </div>

            {/* Active Tools Section */}
            <div>
              <div className="space-y-1">
                {activeTools.map((item) => {
                  const toolActive = item.href ? isActiveToolLink(item) : false;
                  const linkClassName = `${
                    item.badge ? SIDEBAR_ROW_GRID : SIDEBAR_ROW_FLEX
                  } ${
                    toolActive
                      ? "text-white bg-gray-800 shadow-sm"
                      : "text-gray-300 hover:text-white hover:bg-gray-800/50"
                  }`;
                  const toolLabel = (
                    <>
                      {toolActive && (
                        <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-emerald-600" />
                      )}
                      <SidebarIconRail active={toolActive}>{item.icon}</SidebarIconRail>
                      {item.badge ? (
                        <div
                          className={sidebarLabelCellClass(
                            labelsExpanded,
                            "justify-between gap-1",
                            labelsPreHydrate,
                          )}
                        >
                          <span className="truncate text-sm font-medium tracking-wide">
                            {item.name}
                          </span>
                          <span className="flex shrink-0 items-center justify-center p-1.5">
                            <SidebarNavBadge label={item.badge} plain />
                          </span>
                        </div>
                      ) : (
                        <SidebarLabelPanel
                          expanded={labelsExpanded}
                          preHydrate={labelsPreHydrate}
                        >
                          <span className="truncate text-sm font-medium tracking-wide">
                            {item.name}
                          </span>
                        </SidebarLabelPanel>
                      )}
                    </>
                  );

                  if (item.hasDropdown && item.children) {
                    const childRouteActive = item.children.some(
                      (c) => c.href && isSidebarChildActive(c),
                    );
                    const sectionPrefixActive =
                      typeof item.dropdownSectionPrefix === "string" &&
                      pathname.startsWith(item.dropdownSectionPrefix);
                    const dropdownActive =
                      childRouteActive || sectionPrefixActive;
                    return (
                      <div
                        key={item.id}
                        onMouseEnter={() => {
                          if (submenuHoverCloseTimer.current) {
                            clearTimeout(submenuHoverCloseTimer.current);
                            submenuHoverCloseTimer.current = null;
                          }
                        }}
                        onMouseLeave={scheduleSubmenuHoverClose}
                      >
                        <div
                          className={`group relative ${SIDEBAR_TWO_COL_GRID} items-center rounded-lg transition-colors duration-200 ${
                            dropdownActive
                              ? "text-white bg-gray-800 shadow-sm"
                              : "text-gray-300 hover:text-white hover:bg-gray-800/50"
                          }`}
                        >
                          {dropdownActive && (
                            <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-emerald-600" />
                          )}
                          <button
                            type="button"
                            className="flex items-center justify-center border-0 bg-transparent py-2.5 text-inherit cursor-pointer rounded-lg hover:bg-gray-800/40"
                            onClick={() => toggleShellMenu(item.id)}
                            aria-label={
                              compactNav ? "Expand menu" : "Collapse menu"
                            }
                            {...compactFlyoutProps(item)}
                          >
                            <div
                              className={`flex-shrink-0 ${dropdownActive ? "text-emerald-500" : ""}`}
                            >
                              {item.icon}
                            </div>
                          </button>
                          <div
                            className={`${sidebarLabelCellClass(
                              labelsExpanded,
                              "justify-between gap-1",
                              labelsPreHydrate,
                            )}`}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-2 truncate border-0 bg-transparent py-2.5 pr-1 text-left text-inherit cursor-pointer rounded-lg hover:bg-gray-800/40"
                              onClick={() => toggleExpanded(item.id)}
                              aria-expanded={expandedItems.includes(item.id)}
                              aria-controls={`sidebar-submenu-${item.id}`}
                              tabIndex={labelsExpanded ? 0 : -1}
                            >
                              <span className="truncate text-[14px] font-medium tracking-wide">
                                {item.name}
                              </span>
                              {item.badge ? (
                                <SidebarNavBadge label={item.badge} />
                              ) : null}
                            </button>
                            <button
                              type="button"
                              className="flex shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-1.5 text-inherit cursor-pointer hover:bg-gray-700/50"
                              onMouseEnter={() => {
                                if (labelsExpanded) openExpandedSection(item.id);
                              }}
                              onClick={() => toggleExpanded(item.id)}
                              aria-label={
                                expandedItems.includes(item.id)
                                  ? "Collapse submenu"
                                  : "Expand submenu"
                              }
                              tabIndex={labelsExpanded ? 0 : -1}
                            >
                              <svg
                                className={`h-4 w-4 transition-transform duration-200 ${
                                  expandedItems.includes(item.id) ? "rotate-180" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {expandedItems.includes(item.id) &&
                          item.children &&
                          labelsExpanded && (
                            <SidebarSubmenu id={`sidebar-submenu-${item.id}`}>
                              {item.children.map((child) =>
                                child.href ? (
                                  <Link
                                    key={child.id}
                                    href={child.href}
                                    prefetch={false}
                                    onClick={() => {
                                      hoverLockedClosedRef.current.delete(item.id);
                                      openExpandedSection(item.id);
                                      closeMobileSidebar();
                                    }}
                                    className={`relative flex items-center gap-2 rounded-md py-1.5 text-xs font-medium tracking-wide transition-colors ${
                                      isSidebarChildActive(child)
                                        ? "bg-gray-800 text-white"
                                        : "text-gray-400 hover:bg-gray-800/20 hover:text-gray-300"
                                    }`}
                                    aria-current={
                                      isSidebarChildActive(child)
                                        ? "page"
                                        : undefined
                                    }
                                    rel="noopener noreferrer"
                                  >
                                    {isSidebarChildActive(child) && (
                                      <div className="absolute -left-3 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-emerald-600" />
                                    )}
                                    {child.iconSrc ? (
                                      <Image
                                        src={child.iconSrc}
                                        alt={child.iconAlt ?? ""}
                                        width={16}
                                        height={16}
                                        className="h-4 w-4 flex-shrink-0 rounded-sm object-contain"
                                      />
                                    ) : null}
                                    <span className="truncate">{child.name}</span>
                                  </Link>
                                ) : (
                                  <div
                                    key={child.id}
                                    className="relative flex items-center gap-2 rounded-md py-1.5 text-xs font-medium tracking-wide text-gray-400"
                                  >
                                    {child.name}
                                  </div>
                                ),
                              )}
                            </SidebarSubmenu>
                          )}
                      </div>
                    );
                  }

                  return (
                    <div key={item.id}>
                      {item.href ? (
                        item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={
                              item.badge
                                ? `${item.name} ${item.badge}`
                                : item.name
                            }
                            className={linkClassName}
                            onClick={closeMobileSidebar}
                            {...compactTipProps(item.name)}
                          >
                            {toolLabel}
                          </a>
                        ) : (
                          <Link
                            href={item.href}
                            className={linkClassName}
                            aria-label={
                              item.badge
                                ? `${item.name} ${item.badge}`
                                : item.name
                            }
                            aria-current={toolActive ? "page" : undefined}
                            onClick={(e) =>
                              handleNavIconClick(e, toolActive)
                            }
                            {...compactTipProps(item.name)}
                            rel="noopener noreferrer"
                          >
                            {toolLabel}
                          </Link>
                        )
                      ) : (
                        <span
                          className={`${linkClassName} cursor-not-allowed opacity-70`}
                          aria-disabled="true"
                          {...compactTipProps(item.name)}
                        >
                          {toolLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming Tools Section */}
            <div className="pt-6">
              {/* {!(isHydrated && compactNav) && (
                <h3 className="px-3 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Upcoming
                </h3>
              )} */}

              <div className="space-y-1">
                {upcomingTools.map((item) => (
                  <div key={item.id}>
                    {item.hasDropdown ? (
                      <div
                        onMouseEnter={() => {
                          if (submenuHoverCloseTimer.current) {
                            clearTimeout(submenuHoverCloseTimer.current);
                            submenuHoverCloseTimer.current = null;
                          }
                        }}
                        onMouseLeave={scheduleSubmenuHoverClose}
                      >
                        <div
                          className={`group relative ${SIDEBAR_TWO_COL_GRID} items-center rounded-lg text-gray-400 transition-colors duration-200 hover:bg-gray-800/30 hover:text-gray-300`}
                        >
                          <button
                            type="button"
                            className="flex items-center justify-center border-0 bg-transparent py-2.5 text-inherit cursor-pointer rounded-lg hover:bg-gray-800/40"
                            onClick={() => toggleShellMenu(item.id)}
                            aria-label={
                              compactNav ? "Expand menu" : "Collapse menu"
                            }
                            {...compactFlyoutProps(item)}
                          >
                            {item.icon}
                          </button>
                          <div
                            className={`${sidebarLabelCellClass(
                              labelsExpanded,
                              "justify-between gap-1",
                              labelsPreHydrate,
                            )}`}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center truncate border-0 bg-transparent py-2.5 pr-1 text-left text-inherit cursor-pointer rounded-lg hover:bg-gray-800/40"
                              onClick={() => toggleExpanded(item.id)}
                              aria-expanded={expandedItems.includes(item.id)}
                              tabIndex={labelsExpanded ? 0 : -1}
                            >
                              <span className="truncate text-sm font-medium tracking-wide">
                                {item.name}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="flex shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-1.5 text-inherit cursor-pointer hover:bg-gray-700/50"
                              onMouseEnter={() => {
                                if (labelsExpanded) openExpandedSection(item.id, "arrow-hover");
                              }}
                              onClick={() => toggleExpanded(item.id)}
                              aria-label={
                                expandedItems.includes(item.id)
                                  ? "Collapse submenu"
                                  : "Expand submenu"
                              }
                              tabIndex={labelsExpanded ? 0 : -1}
                            >
                              <svg
                                className={`h-4 w-4 transition-transform duration-200 ${
                                  expandedItems.includes(item.id) ? "rotate-180" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Dropdown Children */}
                        {expandedItems.includes(item.id) &&
                          item.children &&
                          labelsExpanded && (
                            <SidebarSubmenu id={`sidebar-submenu-${item.id}`}>
                              {item.children.map((child) => (
                                <div
                                  key={child.id}
                                  className="relative flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium tracking-wide text-gray-400 transition-colors hover:bg-gray-800/20"
                                >
                                  {child.name}
                                </div>
                              ))}
                            </SidebarSubmenu>
                          )}
                      </div>
                    ) : (
                      <div
                        className={`${SIDEBAR_ROW_FLEX} cursor-not-allowed text-gray-400 hover:bg-gray-800/30`}
                        {...compactTipProps(item.name)}
                      >
                        <SidebarIconRail>{item.icon}</SidebarIconRail>
                        <SidebarLabelPanel
                          expanded={labelsExpanded}
                          preHydrate={labelsPreHydrate}
                        >
                          <span className="truncate text-sm font-medium tracking-wide">
                            {item.name}
                          </span>
                        </SidebarLabelPanel>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="shrink-0 border-t border-gray-800/50">
          <div
            className={`${SIDEBAR_ROW_FLEX} pointer-events-none`}
            aria-hidden="true"
          >
            <SidebarIconRail>
              <div className="h-5 w-5" />
            </SidebarIconRail>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
